import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { breadcrumbJsonLd } from "@/content/schema";
import { GLOSSARY_SORTED } from "@/content/glossary";
import { SITE, abs } from "@/content/siteConfig";

const TITLE = "Paid Media Glossary | Berk Hospitality Group";
const DESCRIPTION =
  "Plain-English definitions of paid media and conversion tracking terms, written from operating experience running Google Ads, Meta Ads and Local Services Ads.";
const PATH = "/glossary";
const URL = abs(PATH);

export const Route = createFileRoute("/glossary/")({
  loader: async () => {
    const { GLOSSARY_SORTED } = await import("@/content/glossary");
    return { terms: GLOSSARY_SORTED.map((t) => ({ term: t.term, definition: t.definition, slug: t.slug })) };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: abs(SITE.ogImagePath) },
      { name: "twitter:image", content: abs(SITE.ogImagePath) },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Glossary", path: PATH },
        ]),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "DefinedTermSet",
          "@id": URL,
          name: "Paid Media Glossary",
          description: DESCRIPTION,
          url: URL,
          hasDefinedTerm: (loaderData?.terms ?? []).map((t) => ({
            "@type": "DefinedTerm",
            name: t.term,
            description: t.definition,
            url: abs(`/glossary/${t.slug}`),
            inDefinedTermSet: URL,
          })),
        }),
      },
    ],
  }),
  component: GlossaryIndex,
});

function GlossaryIndex() {
  return (
    <SiteShell>
      <div className="wrap" style={{ paddingTop: 120, paddingBottom: 120 }}>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <h1
            style={{ fontSize: "clamp(32px, 5vw, 54px)", lineHeight: 1.06, letterSpacing: "-0.02em" }}
          >
            Glossary
          </h1>
          <p className="prose-lead mt-6" style={{ fontSize: 19, lineHeight: 1.55, color: "rgba(0,0,0,0.72)" }}>
            Plain-English definitions of the paid media and conversion tracking terms that come up
            in account reviews, written from operating experience rather than from a help center.
          </p>

          <ul className="mt-14 m-0 list-none p-0">
            {GLOSSARY_SORTED.map((t) => (
              <li key={t.slug} style={{ borderBottom: "1px solid rgba(0,47,167,0.12)" }}>
                <Link
                  to="/glossary/$slug"
                  params={{ slug: t.slug }}
                  className="block transition-colors hover:bg-[rgba(0,47,167,0.03)]"
                  style={{ padding: "22px 0" }}
                >
                  <div style={{ fontSize: 20, letterSpacing: "-0.01em", color: "#002FA7" }}>
                    {t.term}
                  </div>
                  <p className="mt-2" style={{ fontSize: 16, lineHeight: 1.6, color: "rgba(0,0,0,0.72)" }}>
                    {t.definition}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </SiteShell>
  );
}
